import { GetClientsDashboardResponse } from '@/api/clients/get-clients-dashboard'
import { BadgeColorKeys } from '@/components/ui/badge'
import { StatusTabOption } from '@/components/ui/status-tabs'
import { ClientTableStatus } from '@/models/features/clients'

import {
  ClientFilterStatus,
  ClientFilterTabsProps,
  NonPMSConnectedBuildingStatusEnum,
  PMSConnectedBuildingStatusEnum,
} from './clients-table.types'

const CLIENT_STATUS_COLOR_MAP: Record<ClientTableStatus, BadgeColorKeys> = {
  Current: 'blue',
  Future: 'green',
  Notice: 'orange',
  Past: 'neutral',
}

export const getClientStatusColor = (status: ClientTableStatus) => {
  return CLIENT_STATUS_COLOR_MAP[status] || 'neutral'
}

export const getClientsTableFilterTabs = ({
  data,
  isPMSConnectedBuilding,
  t,
}: ClientFilterTabsProps): StatusTabOption<ClientFilterStatus>[] => {
  if (!data) {
    return []
  }

  if (isPMSConnectedBuilding) {
    return [
      { label: t('clients:list.status.all'), value: PMSConnectedBuildingStatusEnum.enum.all },
      {
        color: getClientStatusColor('Future'),
        count: data.futureCount,
        label: t('clients:list.status.future'),
        value: PMSConnectedBuildingStatusEnum.enum.future,
      },
      {
        color: getClientStatusColor('Current'),
        count: data.currentCount,
        label: t('clients:list.status.current'),
        value: PMSConnectedBuildingStatusEnum.enum.current,
      },
      {
        color: getClientStatusColor('Notice'),
        count: data.noticeCount,
        label: t('clients:list.status.notice'),
        value: PMSConnectedBuildingStatusEnum.enum.notice,
      },
      {
        color: getClientStatusColor('Past'),
        count: data.pastCount,
        label: t('clients:list.status.past'),
        value: PMSConnectedBuildingStatusEnum.enum.past,
      },
    ]
  }

  return [
    {
      count: data.activeCount,
      label: t('clients:list.status.active'),
      value: NonPMSConnectedBuildingStatusEnum.enum.active,
    },
    {
      color: 'orange',
      count: data.pendingCount,
      label: t('clients:list.status.pending'),
      value: NonPMSConnectedBuildingStatusEnum.enum.pending,
    },
    {
      count: data.archivedCount,
      label: t('clients:list.status.archived'),
      value: NonPMSConnectedBuildingStatusEnum.enum.archived,
    },
  ]
}

// Note: shared by the main paginated query's `select` enrichment and the select-all-matching
// resolver, so the cutoff logic can't drift between the two. Takes the cutoff as a param (rather
// than importing APP_USER_CUTOFF_DATE_MS from clients-table.constants.tsx) to avoid a circular
// import - that file already imports from this one.
export const isClientAppUser = (lastAppLogin: number | null, cutoffMs: number): boolean =>
  lastAppLogin != null && lastAppLogin >= cutoffMs

export const getPaginationTotal = (
  data: GetClientsDashboardResponse | undefined,
  status: ClientFilterStatus | undefined,
) => {
  if (!data || !status) return 0
  switch (status) {
    case PMSConnectedBuildingStatusEnum.enum.all:
      return data.count
    case PMSConnectedBuildingStatusEnum.enum.future:
      return data.futureCount
    case PMSConnectedBuildingStatusEnum.enum.current:
      return data.currentCount
    case PMSConnectedBuildingStatusEnum.enum.notice:
      return data.noticeCount
    case PMSConnectedBuildingStatusEnum.enum.past:
      return data.pastCount
    case NonPMSConnectedBuildingStatusEnum.enum.active:
      return data.activeCount
    case NonPMSConnectedBuildingStatusEnum.enum.pending:
      return data.pendingCount
    case NonPMSConnectedBuildingStatusEnum.enum.archived:
      return data.archivedCount
    default:
      return 0
  }
}
